import React, { useEffect, useState, useCallback } from 'react';
import {
  Badge, Box, Button, Card, CardBody, CardHeader, FormControl, FormHelperText,
  FormLabel, HStack, Heading, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, NumberDecrementStepper,
  NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper,
  Select, Switch, Table, Tbody, Td, Text, Th, Thead, Tr, VStack, useDisclosure,
  useToast, Tooltip, Box as ListBox,
} from '@chakra-ui/react';
import { AddIcon, CopyIcon, DeleteIcon, RepeatIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import icalSyncService from '../services/icalSyncService';
import roomService from '../services/roomService';
import roomTypeService from '../services/roomTypeService';

const SCOPE_OPTIONS = ['room', 'room_type', 'whole_property'];
const APPLY_TO_OPTIONS = ['room', 'room_type', 'all_rooms'];

const STATUS_COLOR = { ok: 'green', error: 'red', pending: 'gray' };

const emptyFeedForm = { name: '', scope: 'room', roomId: '', roomTypeId: '' };
const emptySourceForm = { name: '', url: '', applyTo: 'all_rooms', roomId: '', roomTypeId: '', pollIntervalMinutes: 30 };

export default function IcalSync() {
  const { t } = useTranslation();
  const toast = useToast();

  const [feeds, setFeeds] = useState([]);
  const [sources, setSources] = useState([]);
  const [events, setEvents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollingId, setPollingId] = useState(null);

  const feedModal = useDisclosure();
  const sourceModal = useDisclosure();
  const [feedForm, setFeedForm] = useState(emptyFeedForm);
  const [sourceForm, setSourceForm] = useState(emptySourceForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [feedsRes, sourcesRes, eventsRes, roomsRes, roomTypesRes] = await Promise.all([
        icalSyncService.listExportFeeds(),
        icalSyncService.listImportSources(),
        icalSyncService.listImportedEvents(),
        roomService.getAll(),
        roomTypeService.getAll(),
      ]);
      setFeeds(feedsRes.feeds || []);
      setSources(sourcesRes.sources || []);
      setEvents(eventsRes.events || []);
      setRooms(roomsRes || []);
      setRoomTypes(roomTypesRes || []);
    } catch (e) {
      setError(t('icalSync.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const copyUrl = (url) => {
    try {
      navigator.clipboard.writeText(url);
      toast({ title: t('icalSync.export.copied'), status: 'success', duration: 2000 });
    } catch (e) { /* clipboard unavailable — nothing more we can do here */ }
  };

  const saveFeed = async () => {
    setSaving(true);
    try {
      const payload = { name: feedForm.name, scope: feedForm.scope };
      if (feedForm.scope === 'room') payload.roomId = Number(feedForm.roomId) || null;
      if (feedForm.scope === 'room_type') payload.roomTypeId = Number(feedForm.roomTypeId) || null;
      await icalSyncService.createExportFeed(payload);
      toast({ title: t('icalSync.messages.feedCreated'), status: 'success' });
      feedModal.onClose();
      setFeedForm(emptyFeedForm);
      load();
    } catch (e) {
      toast({ title: t('icalSync.messages.saveFailed'), description: e?.response?.data?.detail, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleFeedActive = async (feed) => {
    try {
      await icalSyncService.updateExportFeed(feed.id, { isActive: !feed.isActive });
      load();
    } catch (e) {
      toast({ title: t('icalSync.messages.saveFailed'), status: 'error' });
    }
  };

  const deleteFeed = async (feed) => {
    if (!window.confirm(t('icalSync.export.deleteConfirm'))) return;
    try {
      await icalSyncService.deleteExportFeed(feed.id);
      toast({ title: t('icalSync.messages.feedDeleted'), status: 'info' });
      load();
    } catch (e) {
      toast({ title: t('icalSync.messages.saveFailed'), status: 'error' });
    }
  };

  const saveSource = async () => {
    setSaving(true);
    try {
      const payload = {
        name: sourceForm.name,
        url: sourceForm.url,
        applyTo: sourceForm.applyTo,
        pollIntervalMinutes: Number(sourceForm.pollIntervalMinutes) || 30,
      };
      if (sourceForm.applyTo === 'room') payload.roomId = Number(sourceForm.roomId) || null;
      if (sourceForm.applyTo === 'room_type') payload.roomTypeId = Number(sourceForm.roomTypeId) || null;
      await icalSyncService.createImportSource(payload);
      toast({ title: t('icalSync.messages.sourceCreated'), status: 'success' });
      sourceModal.onClose();
      setSourceForm(emptySourceForm);
      load();
    } catch (e) {
      toast({ title: t('icalSync.messages.saveFailed'), description: e?.response?.data?.detail, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleSourceActive = async (source) => {
    try {
      await icalSyncService.updateImportSource(source.id, { isActive: !source.isActive });
      load();
    } catch (e) {
      toast({ title: t('icalSync.messages.saveFailed'), status: 'error' });
    }
  };

  const deleteSource = async (source) => {
    if (!window.confirm(t('icalSync.import.deleteConfirm'))) return;
    try {
      await icalSyncService.deleteImportSource(source.id);
      toast({ title: t('icalSync.messages.sourceDeleted'), status: 'info' });
      load();
    } catch (e) {
      toast({ title: t('icalSync.messages.saveFailed'), status: 'error' });
    }
  };

  const pollNow = async (source) => {
    setPollingId(source.id);
    try {
      await icalSyncService.pollNow(source.id);
      toast({ title: t('icalSync.import.pollSuccess'), status: 'success' });
      load();
    } catch (e) {
      toast({ title: t('icalSync.messages.saveFailed'), status: 'error' });
    } finally {
      setPollingId(null);
    }
  };

  const conflictedEvents = events.filter((ev) => ev.conflict);
  const statusKey = (s) => (s === 'ok' ? 'statusOk' : s === 'error' ? 'statusError' : 'statusPending');

  return (
    <PageShell
      title={t('icalSync.title')}
      subtitle={t('icalSync.subtitle')}
      loading={loading}
      error={error}
    >
      <VStack spacing={6} align="stretch">
        {/* Export feeds */}
        <Card>
          <CardHeader display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Heading size="md">{t('icalSync.export.cardTitle')}</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>{t('icalSync.export.cardHelp')}</Text>
            </Box>
            <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={feedModal.onOpen}>
              {t('icalSync.export.addButton')}
            </Button>
          </CardHeader>
          <CardBody pt={0}>
            {feeds.length === 0 ? (
              <Text color="gray.500" fontSize="sm">{t('icalSync.export.noFeeds')}</Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('icalSync.export.name')}</Th>
                      <Th>{t('icalSync.export.scope')}</Th>
                      <Th>{t('icalSync.export.url')}</Th>
                      <Th>{t('icalSync.export.active')}</Th>
                      <Th>{t('icalSync.export.lastFetched')}</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {feeds.map((feed) => (
                      <Tr key={feed.id}>
                        <Td>{feed.name}</Td>
                        <Td>
                          <Badge>
                            {feed.scope === 'room' && (feed.roomNumber || t('icalSync.export.scopeRoom'))}
                            {feed.scope === 'room_type' && (feed.roomTypeName || t('icalSync.export.scopeRoomType'))}
                            {feed.scope === 'whole_property' && t('icalSync.export.scopeWholeProperty')}
                          </Badge>
                        </Td>
                        <Td maxW="260px">
                          <HStack>
                            <Text fontSize="xs" isTruncated>{feed.url}</Text>
                            <IconButton
                              aria-label={t('icalSync.export.copyUrl')}
                              icon={<CopyIcon />} size="xs" onClick={() => copyUrl(feed.url)}
                            />
                          </HStack>
                        </Td>
                        <Td>
                          <Switch isChecked={feed.isActive} onChange={() => toggleFeedActive(feed)} size="sm" />
                        </Td>
                        <Td fontSize="xs">
                          {feed.lastFetchedAt
                            ? new Date(feed.lastFetchedAt).toLocaleString()
                            : t('icalSync.export.neverFetched')}
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost"
                            onClick={() => deleteFeed(feed)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
            <Text fontSize="xs" color="gray.500" mt={3}>{t('icalSync.export.scopeWholePropertyHelp')}</Text>
            <Box mt={4} p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="xs" fontWeight="bold" mb={1}>{t('icalSync.export.platformHints.title')}</Text>
              <Text fontSize="xs">{t('icalSync.export.platformHints.airbnb')}</Text>
              <Text fontSize="xs">{t('icalSync.export.platformHints.vrbo')}</Text>
              <Text fontSize="xs">{t('icalSync.export.platformHints.google')}</Text>
            </Box>
          </CardBody>
        </Card>

        {/* Import sources */}
        <Card>
          <CardHeader display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Heading size="md">{t('icalSync.import.cardTitle')}</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>{t('icalSync.import.cardHelp')}</Text>
            </Box>
            <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={sourceModal.onOpen}>
              {t('icalSync.import.addButton')}
            </Button>
          </CardHeader>
          <CardBody pt={0}>
            {sources.length === 0 ? (
              <Text color="gray.500" fontSize="sm">{t('icalSync.import.noSources')}</Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('icalSync.import.name')}</Th>
                      <Th>{t('icalSync.import.applyTo')}</Th>
                      <Th>{t('icalSync.import.status')}</Th>
                      <Th>{t('icalSync.import.lastPolled')}</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sources.map((source) => (
                      <Tr key={source.id}>
                        <Td>{source.name}</Td>
                        <Td>
                          <Badge>
                            {source.applyTo === 'room' && (source.roomNumber || t('icalSync.import.applyToRoom'))}
                            {source.applyTo === 'room_type' && (source.roomTypeName || t('icalSync.import.applyToRoomType'))}
                            {source.applyTo === 'all_rooms' && t('icalSync.import.applyToAllRooms')}
                          </Badge>
                        </Td>
                        <Td>
                          <Tooltip label={source.lastError || ''} isDisabled={!source.lastError}>
                            <Badge colorScheme={source.isQuarantined ? 'red' : STATUS_COLOR[source.lastStatus] || 'gray'}>
                              {source.isQuarantined
                                ? t('icalSync.import.statusQuarantined')
                                : t(`icalSync.import.${statusKey(source.lastStatus)}`)}
                            </Badge>
                          </Tooltip>
                        </Td>
                        <Td fontSize="xs">
                          {source.lastPolledAt
                            ? new Date(source.lastPolledAt).toLocaleString()
                            : t('icalSync.import.neverPolled')}
                        </Td>
                        <Td>
                          <HStack>
                            <IconButton
                              aria-label={t('icalSync.import.pollNow')} icon={<RepeatIcon />} size="xs"
                              isLoading={pollingId === source.id} onClick={() => pollNow(source)}
                            />
                            <Switch isChecked={source.isActive} onChange={() => toggleSourceActive(source)} size="sm" />
                            <IconButton
                              aria-label="delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost"
                              onClick={() => deleteSource(source)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
            <Box mt={4} p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="xs" fontWeight="bold" mb={1}>{t('icalSync.import.platformHints.title')}</Text>
              <Text fontSize="xs">{t('icalSync.import.platformHints.airbnb')}</Text>
              <Text fontSize="xs">{t('icalSync.import.platformHints.vrbo')}</Text>
              <Text fontSize="xs">{t('icalSync.import.platformHints.google')}</Text>
            </Box>

            {/* Conflicts */}
            <Box mt={5}>
              <Heading size="sm">{t('icalSync.import.conflicts.title')}</Heading>
              <Text fontSize="xs" color="gray.500" mb={2}>{t('icalSync.import.conflicts.help')}</Text>
              {conflictedEvents.length === 0 ? (
                <Text fontSize="sm" color="gray.500">{t('icalSync.import.conflicts.empty')}</Text>
              ) : (
                <ListBox>
                  {conflictedEvents.map((ev) => (
                    <Box key={ev.id} borderWidth="1px" borderColor="red.200" bg="red.50" borderRadius="md" p={2} mb={2}>
                      <Text fontSize="sm" fontWeight="bold">{ev.sourceName} — {ev.dtstart} .. {ev.dtend}</Text>
                      <Text fontSize="xs" whiteSpace="pre-wrap">{ev.conflictDetail}</Text>
                    </Box>
                  ))}
                </ListBox>
              )}
            </Box>
          </CardBody>
        </Card>
      </VStack>

      {/* New export feed modal */}
      <Modal isOpen={feedModal.isOpen} onClose={feedModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('icalSync.export.addButton')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl isRequired>
                <FormLabel>{t('icalSync.export.name')}</FormLabel>
                <Input value={feedForm.name} onChange={(e) => setFeedForm({ ...feedForm, name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('icalSync.export.scope')}</FormLabel>
                <Select value={feedForm.scope} onChange={(e) => setFeedForm({ ...feedForm, scope: e.target.value })}>
                  {SCOPE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{t(`icalSync.export.scope${s === 'room' ? 'Room' : s === 'room_type' ? 'RoomType' : 'WholeProperty'}`)}</option>
                  ))}
                </Select>
                {feedForm.scope === 'whole_property' && (
                  <FormHelperText>{t('icalSync.export.scopeWholePropertyHelp')}</FormHelperText>
                )}
              </FormControl>
              {feedForm.scope === 'room' && (
                <FormControl isRequired>
                  <FormLabel>{t('icalSync.export.room')}</FormLabel>
                  <Select
                    placeholder={t('icalSync.export.selectRoom')}
                    value={feedForm.roomId}
                    onChange={(e) => setFeedForm({ ...feedForm, roomId: e.target.value })}
                  >
                    {rooms.map((r) => <option key={r.id} value={r.id}>{r.number}</option>)}
                  </Select>
                </FormControl>
              )}
              {feedForm.scope === 'room_type' && (
                <FormControl isRequired>
                  <FormLabel>{t('icalSync.export.roomType')}</FormLabel>
                  <Select
                    placeholder={t('icalSync.export.selectRoomType')}
                    value={feedForm.roomTypeId}
                    onChange={(e) => setFeedForm({ ...feedForm, roomTypeId: e.target.value })}
                  >
                    {roomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                  </Select>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={feedModal.onClose}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" isLoading={saving} onClick={saveFeed}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New import source modal */}
      <Modal isOpen={sourceModal.isOpen} onClose={sourceModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('icalSync.import.addButton')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl isRequired>
                <FormLabel>{t('icalSync.import.name')}</FormLabel>
                <Input value={sourceForm.name} onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('icalSync.import.url')}</FormLabel>
                <Input
                  placeholder={t('icalSync.import.urlPlaceholder')}
                  value={sourceForm.url}
                  onChange={(e) => setSourceForm({ ...sourceForm, url: e.target.value })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('icalSync.import.applyTo')}</FormLabel>
                <Select value={sourceForm.applyTo} onChange={(e) => setSourceForm({ ...sourceForm, applyTo: e.target.value })}>
                  {APPLY_TO_OPTIONS.map((a) => (
                    <option key={a} value={a}>{t(`icalSync.import.applyTo${a === 'room' ? 'Room' : a === 'room_type' ? 'RoomType' : 'AllRooms'}`)}</option>
                  ))}
                </Select>
                {sourceForm.applyTo === 'all_rooms' && (
                  <FormHelperText>{t('icalSync.import.applyToAllRoomsHelp')}</FormHelperText>
                )}
              </FormControl>
              {sourceForm.applyTo === 'room' && (
                <FormControl isRequired>
                  <FormLabel>{t('icalSync.export.room')}</FormLabel>
                  <Select
                    placeholder={t('icalSync.export.selectRoom')}
                    value={sourceForm.roomId}
                    onChange={(e) => setSourceForm({ ...sourceForm, roomId: e.target.value })}
                  >
                    {rooms.map((r) => <option key={r.id} value={r.id}>{r.number}</option>)}
                  </Select>
                </FormControl>
              )}
              {sourceForm.applyTo === 'room_type' && (
                <FormControl isRequired>
                  <FormLabel>{t('icalSync.export.roomType')}</FormLabel>
                  <Select
                    placeholder={t('icalSync.export.selectRoomType')}
                    value={sourceForm.roomTypeId}
                    onChange={(e) => setSourceForm({ ...sourceForm, roomTypeId: e.target.value })}
                  >
                    {roomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                  </Select>
                </FormControl>
              )}
              <FormControl>
                <FormLabel>{t('icalSync.import.pollInterval')}</FormLabel>
                <NumberInput
                  min={5} max={1440} value={sourceForm.pollIntervalMinutes}
                  onChange={(v) => setSourceForm({ ...sourceForm, pollIntervalMinutes: v })}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={sourceModal.onClose}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" isLoading={saving} onClick={saveSource}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
}
